import type { ManufacturingPressConsidered } from "@shared-core/data/manufacturing-setup-types";
import { formatCurrencyGbp } from "@shared-core/domain/formatters";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../primitives/table";
import { Badge } from "../../primitives/badge";

/**
 * "Presses considered" — shared by the product-type part-level table (no
 * costs: a product type isn't costed for a specific job) and the cost-pair
 * page's per-line "Alternatives" expander (`DictionaryCostPairPage.tsx`),
 * which passes `wholeRouteCostPence` on each press and gets the extra
 * column. The two press shapes (`ManufacturingPressConsidered` and the
 * validation domain's `PressConsidered`) are structurally compatible —
 * `PressConsidered` simply adds the optional cost field this table already
 * knows how to render.
 */
export interface PressConsideredRow {
  pressCode: string;
  pressName: string;
  site: string;
  outcome: ManufacturingPressConsidered["outcome"];
  reason: string;
  /** Present (even if `null`) only when the caller wants the cost column —
   *  a specific job's whole-route cost, not meaningful at product-type
   *  grain. */
  wholeRouteCostPence?: number | null;
}

const OUTCOME_LABEL: Record<ManufacturingPressConsidered["outcome"], string> = {
  chosen: "Chosen",
  suitable_not_chosen: "Suitable — not chosen",
  excluded: "Excluded",
};

const OUTCOME_TONE: Record<
  ManufacturingPressConsidered["outcome"],
  "success" | "neutral" | "error"
> = {
  chosen: "success",
  suitable_not_chosen: "neutral",
  excluded: "error",
};

export function ManufacturingPressesConsideredTable({
  exampleSpec,
  presses,
}: {
  exampleSpec: string;
  presses: PressConsideredRow[];
}) {
  if (presses.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
        No example specification recorded for this part yet.
      </p>
    );
  }
  const showCost = presses.some((p) => p.wholeRouteCostPence !== undefined);
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        Example spec:{" "}
        <span className="font-medium text-foreground">{exampleSpec}</span>
      </p>
      <div className="overflow-x-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Press</TableHead>
              <TableHead>Site</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Reason</TableHead>
              {showCost && (
                <TableHead className="text-right">
                  Whole-route cost (ours)
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {presses.map((press) => (
              <TableRow key={press.pressCode}>
                <TableCell className="font-medium">{press.pressCode}</TableCell>
                <TableCell className="text-muted-foreground">
                  {press.pressName} · {press.site}
                </TableCell>
                <TableCell>
                  <Badge variant={OUTCOME_TONE[press.outcome]}>
                    {OUTCOME_LABEL[press.outcome]}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {press.reason}
                </TableCell>
                {showCost && (
                  <TableCell className="text-right tabular-nums">
                    {press.wholeRouteCostPence == null
                      ? "—"
                      : formatCurrencyGbp(press.wholeRouteCostPence / 100, 2)}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
